import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { AgChartOptions } from 'ag-charts-enterprise';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateColor } from '../../../../constants/colors';
import { isTruckContextEnabled } from '../../../../constants/helpers/truck';
import { getTruckModeLabel } from '../../../../constants/labels';
import { useSitesTrucksSupervisionQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { DailyTruckRide } from '../../../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { truckMode } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import GenericAgChart from '../../../charts/GenericAgChart';
import LicensePlate from '../../../LicensePlate';
import Icon from '../../../shared/Icon';
import ReportItemCard from '../../../shared/ReportItemCard';
import ScrollPageContainer from '../../../shared/ScrollPageContainer';
import SectionCard from '../../../shared/SectionCard';

interface TrucksLiveSupervisionProps {
    date: Date;
    onlyMySites?: boolean;
}

const TrucksLiveSupervision: React.FC<TrucksLiveSupervisionProps> = ({ date, onlyMySites = false }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        data: dataTrucksSupervision,
        isLoading: isLoadingTrucksSupervision,
        isError: isErrorTrucksSupervision,
    } = useSitesTrucksSupervisionQuery({ date: date.toISOString() });

    const trucksSupervision = useMemo(() => {
        return (
            dataTrucksSupervision
                ?.filter((value) => !onlyMySites || [value.site.conducId?._id, value.site.assistantConducId?._id].includes(user?._id))
                .sort((a, b) => {
                    const minDisplayOrderA = Math.min(
                        ...a.trucks.map((truck) => truck.truck?.truckTypeId?.displayOrder ?? Infinity).filter((order) => order !== Infinity)
                    );
                    const minDisplayOrderB = Math.min(
                        ...b.trucks.map((truck) => truck.truck?.truckTypeId?.displayOrder ?? Infinity).filter((order) => order !== Infinity)
                    );

                    if (minDisplayOrderA === Infinity) return 1;
                    if (minDisplayOrderB === Infinity) return -1;

                    if (minDisplayOrderA !== minDisplayOrderB) return minDisplayOrderA - minDisplayOrderB;
                    return a.site.name.localeCompare(b.site.name);
                }) || []
        );
    }, [dataTrucksSupervision, onlyMySites]);

    const summary = useMemo(() => {
        if (!trucksSupervision) {
            return { totalRides: 0, totalDeparture: 0, totalArrival: 0 };
        }

        let totalRides = 0;
        let totalDeparture = 0;
        let totalRidesAppro = 0;
        let totalArrival = 0;
        let totalRidesEvac = 0;

        trucksSupervision.forEach((siteData) => {
            siteData.trucks.forEach((truck) => {
                totalRides += truck.rides.length;
                totalDeparture += truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0);
                totalRidesAppro += truck.rides.filter((r) => r.mode === truckMode.APPRO).length;
                totalArrival += truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0);
                totalRidesEvac += truck.rides.filter((r) => r.mode === truckMode.EVAC).length;
            });
        });

        return { totalRides, totalDeparture, totalArrival, totalRidesAppro, totalRidesEvac };
    }, [trucksSupervision]);

    const chartOptions = useMemo((): AgChartOptions | null => {
        if (!trucksSupervision || trucksSupervision.length === 0) {
            return null;
        }

        const chartData: any[] = [];

        trucksSupervision.forEach((siteData) => {
            const totalDeparture = siteData.trucks.reduce(
                (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0),
                0
            );
            const totalArrival = siteData.trucks.reduce((acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0), 0);

            chartData.push({
                site: siteData.site.name,
                appro: totalDeparture,
                evac: totalArrival,
            });
        });

        return {
            data: chartData,
            series: [
                {
                    type: 'bar',
                    xKey: 'site',
                    yKey: 'appro',
                    yName: 'Tonnage Appro',
                    fill: '#00b8c2',
                    strokeWidth: 0,
                },
                {
                    type: 'bar',
                    xKey: 'site',
                    yKey: 'evac',
                    yName: 'Tonnage Évac',
                    fill: 'lightgreen',
                    strokeWidth: 0,
                },
            ],
            axes: [
                {
                    type: 'category',
                    position: 'bottom',
                },
                {
                    type: 'number',
                    position: 'left',
                    label: {
                        formatter: ({ value }) => `${formatNumber(value, t('locale'), 2)} t`,
                    },
                },
            ],
        };
    }, [trucksSupervision, t]);

    const formatTime = (date: Date | undefined) => {
        if (!date) return '-';
        return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <ScrollPageContainer loading={isLoadingTrucksSupervision}>
            {chartOptions && dataTrucksSupervision && (
                <Paper sx={{ p: 2, mb: 1 }}>
                    <Stack direction="row" spacing={4} justifyContent="space-around" flexWrap={'wrap'}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box textAlign="center">
                                <Typography variant="h4" color="primary">
                                    {summary.totalRidesAppro}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tours Appro
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography variant="h4" sx={{ color: '#00b8c2' }}>
                                    {formatNumber(summary.totalDeparture, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tonnage Appro
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={4} justifyContent="center">
                            <Box textAlign="center">
                                <Typography variant="h4" color="primary">
                                    {summary.totalRidesEvac}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tours Evac
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography variant="h4" sx={{ color: 'lightgreen' }}>
                                    {formatNumber(summary.totalArrival, t('locale'), 2)} t
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Tonnage Évac
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                    <GenericAgChart options={chartOptions} />
                </Paper>
            )}

            {trucksSupervision.map((siteData) => {
                const siteTotalRidesAppro = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.filter((r) => r.mode === truckMode.APPRO).length,
                    0
                );
                const siteTotalRidesEvac = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.filter((r) => r.mode === truckMode.EVAC).length,
                    0
                );
                const siteTotalDeparture = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0),
                    0
                );
                const siteTotalArrival = siteData.trucks.reduce(
                    (acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0),
                    0
                );
                return (
                    <SectionCard
                        key={siteData.site._id}
                        title={siteData.site.name}
                        iconTitleName="place"
                        hideTotalPrice
                        iconTitleColor={siteData.site.conducId?.color}
                        extraActions={
                            <Stack direction="row" spacing={2} alignItems="baseline" flexWrap={'wrap'} pr={1}>
                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" gap={0.5} width={80}>
                                    {!!siteTotalRidesAppro && (
                                        <>
                                            <Typography variant="h5" color="primary" fontWeight="bold">
                                                {siteTotalRidesAppro}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Appro
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" gap={0.5} width={120}>
                                    {!!siteTotalDeparture && (
                                        <>
                                            <Typography variant="h5" sx={{ color: '#00b8c2', fontWeight: 'bold' }}>
                                                {formatNumber(siteTotalDeparture, t('locale'), 2)} t
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Appro
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" gap={0.5} width={80}>
                                    {!!siteTotalRidesEvac && (
                                        <>
                                            <Typography variant="h5" color="primary" fontWeight="bold">
                                                {siteTotalRidesEvac}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Evac
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" gap={0.5} width={120}>
                                    {!!siteTotalArrival && (
                                        <>
                                            <Typography variant="h5" sx={{ color: 'lightgreen', fontWeight: 'bold' }}>
                                                {formatNumber(siteTotalArrival, t('locale'), 2)} t
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Evac
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Stack>
                        }
                    >
                        {siteData.trucks
                            .sort((a, b) => {
                                const displayOrderA = a.truck?.truckTypeId?.displayOrder ?? Infinity;
                                const displayOrderB = b.truck?.truckTypeId?.displayOrder ?? Infinity;
                                if (displayOrderA !== displayOrderB) return displayOrderA - displayOrderB;

                                const getEarliest = (rides: DailyTruckRide[]) => {
                                    if (!rides?.length) return Infinity;
                                    const times = rides.map((r) => (r.time ? new Date(r.time).getTime() : Infinity)); // fallback Infinity si r.time undefined
                                    return Math.min(...times);
                                };

                                const earliestA = getEarliest(a.rides);
                                const earliestB = getEarliest(b.rides);

                                if (earliestA !== earliestB) return earliestA - earliestB;

                                return a.truck?.designation.localeCompare(b.truck?.designation) || 0;
                            })
                            .map((truck) => {
                                const truckTotalDeparture = truck.rides.reduce((acc, r) => acc + (r.departureTonnage || 0), 0);
                                const truckTotalRidesAppro = truck.rides.reduce((acc, r) => acc + (r.mode === truckMode.APPRO ? 1 : 0), 0);
                                const truckTotalArrival = truck.rides.reduce((acc, r) => acc + (r.arrivalTonnage || 0), 0);
                                const truckTotalRidesEvac = truck.rides.reduce((acc, r) => acc + (r.mode === truckMode.EVAC ? 1 : 0), 0);
                                const truckTotalPrice = truckTotalDeparture + truckTotalArrival;

                                return (
                                    <ReportItemCard
                                        key={truck.truck._id}
                                        titleComponent={<LicensePlate number={truck.truck?.plateNumber || ''} />}
                                        title={`${truck.truck?.truckTypeId.designation || ''} ${!!truck.truck.truckCompanyId.isOwnCompany ? truck.truck.userId?.username || '' : truck.truck.truckCompanyId.designation}`}
                                        price={truckTotalPrice}
                                        disabled={true}
                                        hidePrice
                                        hideMajorAction
                                        extraInfo={
                                            <Stack direction="row" spacing={2} alignItems="center" flex={1} flexWrap={'wrap'}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        width: {
                                                            xs: '100%',
                                                            sm: '90%',
                                                            md: 720,
                                                        },

                                                        flexWrap: 'wrap',
                                                        justifyContent: 'flex-start',
                                                    }}
                                                >
                                                    <Paper
                                                        sx={{
                                                            width: 75,
                                                            borderRadius: 1,
                                                            p: 0.5,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                        elevation={3}
                                                    >
                                                        <Typography variant="body2">
                                                            {truck.rides.length} {t('ride(s)')}
                                                        </Typography>
                                                    </Paper>
                                                    {truck.rides
                                                        .sort(
                                                            (a, b) =>
                                                                (a.time ? new Date(a.time).getTime() : 0) - (b.time ? new Date(b.time).getTime() : 0)
                                                        )
                                                        .slice(0, 10)
                                                        .map((ride) => (
                                                            <Paper
                                                                key={ride._id}
                                                                sx={{
                                                                    minWidth: 55,
                                                                    height: 35,
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                }}
                                                                elevation={3}
                                                            >
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    {ride.time ? formatTime(new Date(ride.time)) : '???'}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    {ride.departureTonnage && (
                                                                        <Typography variant="caption" sx={{ color: '#00b8c2', fontWeight: 'bold' }}>
                                                                            {ride.departureTonnage} t
                                                                        </Typography>
                                                                    )}
                                                                    {ride.arrivalTonnage && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{ color: 'lightgreen', fontWeight: 'bold' }}
                                                                        >
                                                                            {ride.arrivalTonnage} t
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Paper>
                                                        ))}

                                                    {truck.rides.length > 10 && (
                                                        <Paper
                                                            sx={{
                                                                width: 50,
                                                                borderRadius: 1,
                                                                p: 0.5,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                            elevation={3}
                                                        >
                                                            <Typography variant="body2">+{truck.rides.length - 10}</Typography>
                                                        </Paper>
                                                    )}
                                                </Box>

                                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" width={80} gap={0.5}>
                                                    {!!truckTotalRidesAppro && (
                                                        <>
                                                            <Typography variant="h6" color="primary">
                                                                {truckTotalRidesAppro}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Appro
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" width={120} gap={0.5}>
                                                    {!!truckTotalDeparture && (
                                                        <>
                                                            <Typography variant="h6" sx={{ color: '#00b8c2' }}>
                                                                {formatNumber(truckTotalDeparture, t('locale'), 2)} t
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Appro
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" width={80} gap={0.5}>
                                                    {!!truckTotalRidesEvac && (
                                                        <>
                                                            <Typography variant="h6" color="primary">
                                                                {truckTotalRidesEvac}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Evac
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                                <Box display="flex" justifyContent={'flex-end'} alignItems="baseline" width={120} gap={0.5}>
                                                    {!!truckTotalArrival && (
                                                        <>
                                                            <Typography variant="h6" sx={{ color: 'lightgreen' }}>
                                                                {formatNumber(truckTotalArrival, t('locale'), 2)} t
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Evac
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </Stack>
                                        }
                                    >
                                        {truck.rides
                                            .sort((a, b) => {
                                                const timeA = a.time ? new Date(a.time).getTime() : 0;
                                                const timeB = b.time ? new Date(b.time).getTime() : 0;
                                                return timeA - timeB;
                                            })
                                            .map((ride, index) => (
                                                <Paper
                                                    key={ride._id}
                                                    elevation={3}
                                                    sx={{
                                                        p: 1,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            boxShadow: 3,
                                                            transform: 'translateX(4px)',
                                                        },
                                                        gap: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                >
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Chip
                                                                label={`#${index + 1} - ${capitalizeFirstLetter(getTruckModeLabel(ride.mode))}`}
                                                                size="small"
                                                                color="default"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                        </Stack>
                                                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                                                            <Box textAlign="center">
                                                                {!!ride.departureTonnage && (
                                                                    <Typography variant="h6" sx={{ color: '#00b8c2', fontWeight: 'bold' }}>
                                                                        {formatNumber(ride.departureTonnage || 0, t('locale'), 2)} t
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box textAlign="center">
                                                                {!!ride.arrivalTonnage && (
                                                                    <Typography variant="h6" sx={{ color: 'lightgreen', fontWeight: 'bold' }}>
                                                                        {formatNumber(ride.arrivalTonnage || 0, t('locale'), 2)} t
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Stack>

                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                            {isTruckContextEnabled(ride.mode, 'departure') && (
                                                                <>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {ride.departureTime ? formatTime(new Date(ride.departureTime)) : '-'}
                                                                        {' → '}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                            {ride.time && (
                                                                <Chip
                                                                    icon={<Icon name="place" />}
                                                                    label={formatTime(new Date(ride.time))}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                />
                                                            )}
                                                            {isTruckContextEnabled(ride.mode, 'arrival') && (
                                                                <>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {' → '}
                                                                        {ride.arrivalTime ? formatTime(new Date(ride.arrivalTime)) : '-'}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                            {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                                                <>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {' → '}
                                                                        {ride.secondTime ? formatTime(new Date(ride.secondTime)) : '-'}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </Stack>

                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                            {isTruckContextEnabled(ride.mode, 'departure') && (
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {ride.departureSiteId?.name || ride.departureQuarryId?.designation || '-'}
                                                                    {' → '}
                                                                </Typography>
                                                            )}

                                                            <Chip
                                                                icon={<Icon name="place" />}
                                                                label={siteData.site.name || '-'}
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                            />
                                                            {isTruckContextEnabled(ride.mode, 'arrival') && (
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {' → '}
                                                                    {ride.arrivalSiteId?.name || ride.arrivalQuarryId?.designation || '-'}
                                                                </Typography>
                                                            )}
                                                            {isTruckContextEnabled(ride.mode, 'secondTimeOnSite') && (
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {' → '}
                                                                    {siteData.site.name || '-'}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Stack>

                                                    {ride.transfers && ride.transfers.length > 0 && (
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                                {ride.transfers.map((transfer) => (
                                                                    <Stack
                                                                        key={transfer._id}
                                                                        direction="column"
                                                                        spacing={1}
                                                                        sx={{
                                                                            bgcolor: getMaterialStateColor(transfer.state, 'background'),
                                                                            p: 0.5,
                                                                            borderRadius: 1,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={getMaterialStateColor(transfer.state, 'text')}
                                                                        >
                                                                            {transfer.machineId?.designation ||
                                                                                transfer.toolId?.designation ||
                                                                                `${transfer.rentalName} ${transfer.rentalCompanyId?.designation}` ||
                                                                                '-'}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={getMaterialStateColor(transfer.state, 'text')}
                                                                        >
                                                                            {transfer.siteId?.name || transfer.quarryId?.designation || '-'}
                                                                        </Typography>
                                                                    </Stack>
                                                                ))}
                                                            </Stack>
                                                        </Stack>
                                                    )}
                                                </Paper>
                                            ))}
                                    </ReportItemCard>
                                );
                            })}
                    </SectionCard>
                );
            })}
        </ScrollPageContainer>
    );
};

export default TrucksLiveSupervision;
